/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { Button, Text } from '@az/starc-ui';
import styles from '../styles.module.scss';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import { useLabels } from '@/hooks/useLabels';
import { SmartLink } from '@/utils/smartLink';
import { useCallback } from 'react';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { usePageType } from '@/hooks/usePageType';
import { sessionStorage } from '@/utils/sessionStorage';
import type { BuyAgainPurchase } from '@/features/orderHistory';
import { PurchasedDate } from './PurchasedDate';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { pageTypes } from '@/constants/page';
import { analyticsPageType } from '@/constants/analytics/analyticsPageType';

const labelMap = {
  buyAgain: 'label_buy_again',
};

export const BuyAgainProduct = ({
  buyAgainCandidate,
}: {
  buyAgainCandidate?: BuyAgainPurchase;
}) => {
  const labels = useLabels(labelMap);
  const { setPageSpecificData } = usePageSpecificData();
  const { pageType } = usePageType();
  const enableBuyAgainRedesign = useForteDecisionFlagWithTrueDefault('enableBuyAgainRedesign');

  const handleAnalytics = useCallback(() => {
    const pfmPageType =
      pageType === pageTypes.ProductShelf ? analyticsPageType.catalogShelf : pageType;
    sessionStorage.setItem('productType', 'buyAgain');
    setPageSpecificData({
      productFindingMethod: 'Product Recommendation',
      productRecommendationType: 'Buy Again',
      ...(pfmPageType && { pfmLocation: `Buy Again: ${pfmPageType}` }),
    });
  }, [pageType, setPageSpecificData]);

  if (enableBuyAgainRedesign && buyAgainCandidate) {
    return (
      <FlexGrid flexDirection="column" gap={3}>
        <PurchasedDate purchasedDate={buyAgainCandidate.transactionDate} />
        <Text variant="small-body" className={styles.productName}>
          {buyAgainCandidate.skuDisplayName}
        </Text>
      </FlexGrid>
    );
  }

  if (!buyAgainCandidate) {
    return null;
  }

  return (
    <>
      <Text variant="small-body" className={styles.productName}>
        {buyAgainCandidate.skuDisplayName}
      </Text>
      <FlexGrid justifyContent={'end'}>
        <SmartLink to={buyAgainCandidate.detailUrl}>
          <Button
            variant={'pill-secondary'}
            className={styles.actionButton}
            onClick={handleAnalytics}
          >
            {labels.buyAgain}
          </Button>
        </SmartLink>
      </FlexGrid>
    </>
  );
};
